import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export default function CategoriesPanel({ open, onClose, categories }) {
  const navigate = useNavigate();
  const [expandedParent, setExpandedParent] = useState(null);

  const roots = categories.filter(c => !c.parent_id);
  const childrenOf = (id) => categories.filter(c => c.parent_id === id);

  const handleClose = () => { setExpandedParent(null); onClose(); };

  const goTo = (cat) => {
    navigate(`/category/${cat.id}`, { state: { categoryId: cat.id, categoryName: cat.name } });
    handleClose();
  };

  const CatIcon = ({ cat }) => cat.icon_url ? (
    <img src={cat.icon_url} alt={cat.name} className="w-10 h-10 object-contain" />
  ) : (
    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
      <span className="text-primary font-bold text-sm">{cat.name?.charAt(0)}</span>
    </div>
  );

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[70vh] overflow-y-auto"
          >
            <div className="px-6 pt-5 pb-2 flex items-center justify-between sticky top-0 bg-white z-10 border-b border-border">
              <div className="flex items-center gap-2">
                {expandedParent && (
                  <button
                    data-testid="categories-panel-back"
                    onClick={() => setExpandedParent(null)}
                    className="p-1.5 rounded-full hover:bg-muted transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-secondary" />
                  </button>
                )}
                <h3 className="text-lg font-bold text-secondary">
                  {expandedParent ? expandedParent.name : 'Categorías'}
                </h3>
              </div>
              <button onClick={handleClose} className="p-2 rounded-full hover:bg-muted transition-colors">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {!expandedParent ? (
              <div className="grid grid-cols-3 gap-3 p-5">
                {roots.map((cat) => {
                  const subs = childrenOf(cat.id);
                  return (
                    <button
                      key={cat.id}
                      data-testid={`category-panel-${cat.id}`}
                      onClick={() => subs.length > 0 ? setExpandedParent(cat) : goTo(cat)}
                      className="relative flex flex-col items-center gap-2 p-3 rounded-2xl hover:bg-primary/5 transition-colors"
                    >
                      <CatIcon cat={cat} />
                      <span className="text-xs font-medium text-secondary text-center leading-tight line-clamp-2">{cat.name}</span>
                      {subs.length > 0 && (
                        <span className="absolute top-1 right-1 text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                          {subs.length}<ChevronRight className="w-2.5 h-2.5" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="p-5 space-y-2">
                <button
                  data-testid={`category-panel-all-${expandedParent.id}`}
                  onClick={() => goTo(expandedParent)}
                  className="w-full flex items-center gap-3 p-3 rounded-2xl bg-primary/5 hover:bg-primary/10 transition-colors"
                >
                  <CatIcon cat={expandedParent} />
                  <span className="text-sm font-bold text-primary">Ver todo {expandedParent.name}</span>
                </button>
                {childrenOf(expandedParent.id).map((sub) => (
                  <button
                    key={sub.id}
                    data-testid={`category-panel-${sub.id}`}
                    onClick={() => goTo(sub)}
                    className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-muted transition-colors"
                  >
                    <CatIcon cat={sub} />
                    <span className="text-sm font-semibold text-secondary">{sub.name}</span>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
